import { Link } from 'react-router-dom';
import Logo from '../Logo';
import SocialIcon from '../SocialIcon';
import { SOCIAL_LINKS } from './navConfig';
import { useMenu, isExternalUrl } from '../../lib/useMenu';

function FooterLink({ url, target, children }) {
  const className = 'text-cream-200/80 transition hover:text-gold-400';
  if (isExternalUrl(url)) {
    return (
      <a
        href={url}
        target={target || '_self'}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        className={className}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={url} className={className}>
      {children}
    </Link>
  );
}

const FOOTER_LINKS = [
  {
    heading: 'Explore',
    links: [
      { label: 'About Us', to: '/about' },
      { label: 'Impact Stories', to: '/stories' },
      { label: 'News & Highlights', to: '/news' },
      { label: 'Activities', to: '/activities' },
      { label: 'Events', to: '/events' },
    ],
  },
  {
    heading: 'Engage',
    links: [
      { label: 'Calendar', to: '/calendar' },
      { label: 'Media Center', to: '/media' },
      { label: 'Member Login', to: '/login' },
      { label: 'Contact', to: '/contact' },
      { label: 'Zonta International', to: 'https://www.zonta.org' },
    ],
  },
];

export default function Footer() {
  const { items: menuItems, loaded } = useMenu('footer');
  const useDynamic = loaded && menuItems.length > 0;

  // Build footer groups from the CMS menu: top-level items with children become
  // headed columns; childless top-level items are collected into a "Links" group.
  const dynamicGroups = useDynamic
    ? (() => {
        const groups = menuItems
          .filter((it) => it.children.length > 0)
          .map((it) => ({ heading: it.label, links: it.children }));
        const flat = menuItems.filter((it) => it.children.length === 0);
        if (flat.length) groups.push({ heading: 'Links', links: flat });
        return groups;
      })()
    : [];

  // NOTE: every `.eyebrow` in here carries an explicit `text-gold-400`.
  // `.eyebrow`'s own colour is `gold-600`, which is the *light-background* text
  // gold — dark enough to clear 4.5:1 on white/cream, and therefore too dark to
  // read on `maroon-950`. `gold-400` is the on-dark counterpart (12.02:1 here),
  // and is what every other dark section on the site already overrides to.
  return (
    <footer className="mt-20 bg-maroon-950 text-cream-200">
      <div className="container-page grid gap-10 py-14 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1.2fr_1.2fr]">
        <div className="sm:col-span-2 lg:col-span-1">
          <Logo light />
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-cream-200/80">
            Zonta District 17 Area 1 unites professionals committed to advancing the status of women
            through service and advocacy across our community and beyond.
          </p>
          <div className="mt-5 flex gap-3">
            {SOCIAL_LINKS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${s.label} (opens in a new tab)`}
                title={s.label}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-gold-300 transition hover:bg-gold-400 hover:text-maroon-900"
              >
                <SocialIcon name={s.icon} label={s.label} className="h-[18px] w-[18px]" />
              </a>
            ))}
          </div>
        </div>

        {useDynamic
          ? dynamicGroups.map((group) => (
              <div key={group.heading}>
                <h4 className="eyebrow mb-4 text-gold-400">{group.heading}</h4>
                <ul className="space-y-2 text-sm">
                  {group.links.map((link) => (
                    <li key={link.id}>
                      <FooterLink url={link.url} target={link.target}>
                        {link.label}
                      </FooterLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          : FOOTER_LINKS.map((group) => (
              <div key={group.heading}>
                <h4 className="eyebrow mb-4 text-gold-400">{group.heading}</h4>
                <ul className="space-y-2 text-sm">
                  {group.links.map((link) => (
                    <li key={link.to}>
                      {isExternalUrl(link.to) ? (
                        <a
                          href={link.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-cream-200/80 transition hover:text-gold-400"
                        >
                          {link.label}
                          <span aria-hidden="true" className="text-[0.85em]">&#8599;</span>
                        </a>
                      ) : (
                        <Link to={link.to} className="text-cream-200/80 transition hover:text-gold-400">
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

        <div>
          <h4 className="eyebrow mb-4 text-gold-400">Contact Us</h4>
          <ul className="space-y-2.5 text-sm text-cream-200/80">
            <li>
              <a href="mailto:zontad17a12026@gmail.com" className="transition hover:text-gold-400">
                zontad17a12026@gmail.com
              </a>
            </li>
            <li>District 17 &ndash; Area 1</li>
            <li>Philippines</li>
          </ul>
        </div>

        <div>
          <h4 className="eyebrow mb-4 text-gold-400">Stay Connected</h4>
          <p className="text-sm leading-relaxed text-cream-200/80">
            Follow us for updates, inspiration, and stories from our clubs.
          </p>
          <Link
            to="/contact"
            className="mt-4 inline-flex items-center rounded-md bg-gold-400 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.14em] text-maroon-900 transition hover:bg-gold-300"
          >
            Subscribe to Updates
          </Link>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-page flex flex-col items-center justify-between gap-2 py-5 text-xs text-cream-200/60 sm:flex-row">
          <p>© {new Date().getFullYear()} Zonta District 17 Area 1. All rights reserved.</p>
          <p>Built with care by the aimers team.</p>
        </div>
      </div>
    </footer>
  );
}
